import { useState } from 'react';
import { logger } from '@/core/services/logger';
import { supabase } from '@/core/services/supabase';
import type { Message } from './useStreamingChat';

export interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  created_at: string;
  updated_at: string;
}

type DrPhillipChatRow = {
  id: string;
  user_id: string;
  client_id: string | null;
  title: string;
  messages: unknown;
  created_at: string;
  updated_at: string;
};

export function useChatHistory() {
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);

  const loadChatSessions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('dr_phillip_chats' as unknown)
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      if (data) {
        const chats = data as unknown as DrPhillipChatRow[];
        setChatSessions(chats.map(chat => ({
          id: chat.id,
          title: chat.title,
          messages: chat.messages as Message[],
          created_at: chat.created_at,
          updated_at: chat.updated_at,
        })));

        if (!currentChatId && chats.length > 0) {
          setCurrentChatId(chats[0].id);
        }
      }
    } catch (error) {
      logger.error('Failed to load chat sessions:', error);
    }
  };

  const saveChat = async (messages: Message[], chatId: string | null) => {
    if (messages.length === 0) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const firstUserMessage = messages.find(m => m.role === 'user');
      const title = firstUserMessage
        ? firstUserMessage.content.slice(0, 50) + (firstUserMessage.content.length > 50 ? '...' : '')
        : 'New Chat';

      if (chatId) {
        const { error } = await supabase
          .from('dr_phillip_chats' as unknown)
          .update({
            messages: messages,
            title: title,
            updated_at: new Date().toISOString()
          })
          .eq('id', chatId);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('dr_phillip_chats' as unknown)
          .insert({
            user_id: user.id,
            title: title,
            messages: messages,
          } as unknown)
          .select()
          .single();

        if (error) throw error;
        if (data) {
          setCurrentChatId((data as unknown as DrPhillipChatRow).id);
        }
      }

      await loadChatSessions();
    } catch (error) {
      logger.error('Failed to save chat:', error);
    }
  };

  const deleteChat = async (chatId: string) => {
    try {
      const { error } = await supabase
        .from('dr_phillip_chats' as unknown)
        .delete()
        .eq('id', chatId);

      if (error) throw error;

      if (currentChatId === chatId) {
        setCurrentChatId(null);
      }

      await loadChatSessions();
    } catch (error) {
      logger.error('Failed to delete chat:', error);
    }
  };

  return {
    chatSessions,
    currentChatId,
    setCurrentChatId,
    loadChatSessions,
    saveChat,
    deleteChat,
  };
}
